import type { Candle, ChartInterval, TimeframeAssessment, TrendState } from '../types/market.js';
import { clamp, percentChange } from './number.js';
import { exponentialMovingAverage, relativeStrengthIndex } from './indicators.js';

function stateFromScore(score: number): TrendState {
  if (score >= 70) return 'strong-bull';
  if (score >= 30) return 'bull';
  if (score >= 10) return 'weak-bull';
  if (score > -10) return 'neutral';
  if (score > -30) return 'weak-bear';
  if (score > -70) return 'bear';
  return 'strong-bear';
}

function splitCandles(candles: Candle[]): { completed: Candle[]; active?: Candle } {
  const latest = candles.at(-1);
  if (!latest) return { completed: [] };
  if (latest.closed) return { completed: candles };
  return { completed: candles.slice(0, -1), active: latest };
}

export function assessTimeframe(interval: ChartInterval, candles: Candle[], maturity = 100): TimeframeAssessment {
  const { completed, active } = splitCandles(candles);
  const current = active ?? completed.at(-1);
  const candleDirection = current
    ? current.close > current.open
      ? 'long'
      : current.close < current.open
        ? 'short'
        : 'neutral'
    : 'neutral';

  if (completed.length < 8 || !current) {
    return {
      interval,
      score: 0,
      state: 'neutral',
      candleDirection,
      candleMaturity: maturity,
      historicalDepth: candles.length,
      completedCandles: completed.length,
      activeVolumePace: 0,
      summary: 'Not enough completed same-timeframe history for structural assessment.',
    };
  }

  const closes = completed.map((candle) => candle.close);
  const ema8 = exponentialMovingAverage(closes, 8).at(-1) ?? closes.at(-1) ?? 0;
  const ema21 = exponentialMovingAverage(closes, Math.min(21, closes.length)).at(-1) ?? closes.at(-1) ?? 0;
  const rsi = relativeStrengthIndex(closes, Math.min(14, Math.max(3, closes.length - 2))).at(-1) ?? 50;
  const recent = completed.slice(-Math.min(14, completed.length));
  const half = Math.max(2, Math.floor(recent.length / 2));
  const earlier = recent.slice(0, half);
  const later = recent.slice(half);
  const earlierHigh = Math.max(...earlier.map((candle) => candle.high));
  const earlierLow = Math.min(...earlier.map((candle) => candle.low));
  const laterHigh = Math.max(...later.map((candle) => candle.high));
  const laterLow = Math.min(...later.map((candle) => candle.low));
  const structureBias = (laterHigh > earlierHigh ? 1 : -1) + (laterLow > earlierLow ? 1 : -1);
  const slopeLookback = Math.min(8, closes.length - 1);
  const slope = percentChange(closes.at(-1) ?? 0, closes.at(-(slopeLookback + 1)) ?? 0);
  const completedVolumeAverage = recent.reduce((sum, candle) => sum + candle.volume, 0) / Math.max(1, recent.length);
  const activeElapsedFraction = clamp(maturity / 100, 0.05, 1);
  const activeVolumePace = active && completedVolumeAverage > 0
    ? clamp((active.volume / activeElapsedFraction) / completedVolumeAverage, 0, 8)
    : 1;
  const activeBodyMove = percentChange(current.close, current.open);
  const activeWeight = active ? clamp(maturity / 100, 0.1, 0.85) : 1;

  let score = 0;
  score += clamp(slope * 8, -28, 28);
  score += (closes.at(-1) ?? 0) > ema8 ? 9 : -9;
  score += ema8 > ema21 ? 12 : -12;
  score += clamp((rsi - 50) * 0.38, -13, 13);
  score += clamp(structureBias * 8, -16, 16);
  score += clamp(activeBodyMove * 8 * activeWeight, -10, 10);
  score += clamp((activeVolumePace - 1) * Math.sign(activeBodyMove) * 5 * activeWeight, -7, 7);
  score = clamp(score, -100, 100);

  const state = stateFromScore(score);
  const pressure = interval === '1d' || interval === '1w' ? 'background pressure' : 'active structure';
  const activeNote = active
    ? `The active candle is ${maturity.toFixed(0)}% mature and its time-normalized volume pace is ${activeVolumePace.toFixed(2)}×.`
    : 'The latest candle is exchange-confirmed closed.';

  return {
    interval,
    score,
    state,
    candleDirection,
    candleMaturity: maturity,
    historicalDepth: candles.length,
    completedCandles: completed.length,
    activeVolumePace,
    summary: `${state.replace('-', ' ')} ${pressure}; RSI ${rsi.toFixed(0)} is supporting context only. ${activeNote}`,
  };
}
